// ============================================
// Lovense Settings UI — v0.2.4+ (Phase 2b)
//
// Self-injecting Toys section inside #overlay-pause. Hidden for free
// builds (Patreon gate). Handles QR pairing flow, test pulse, category
// toggles, intensity slider, disconnect.
//
// Uses events.createScope() for clean teardown if the section is ever
// removed (e.g. on Patreon revocation after the overlay already rendered).
// ============================================

import { isPat } from '../engine/pgate.js';

import { events, Events } from '../engine/events.js';
import { lovenseManager } from '../systems/lovense.js';
import { L } from '../systems/localization.js';
import { settingsUI } from './settings.js';

// Health-poll interval while the Toys section is mounted AND the overlay is
// open. 10s is low enough that the user sees a disconnect reflected within a
// few seconds of toggling off on the toy side, high enough to not spam the
// VPS. Only runs when the pause overlay is actually visible so we don't
// poll in the background after the user closes Settings.
const HEALTH_POLL_INTERVAL_MS = 10000;

class LovenseSettingsUI {
    constructor() {
        this._mounted = false;
        this._root = null;        // .pause-lovense section
        this._modal = null;       // #overlay-lovense-qr
        this._scope = null;       // events scope for teardown
        this._pollHandle = null;  // in-flight waitForPairing promise
        this._healthTimer = null; // setInterval handle for periodic health poll
    }

    /**
     * Mount the Toys section into the pause overlay. Called lazily when
     * the pause overlay first opens. Safe to call multiple times —
     * subsequent calls refresh the UI state without rebuilding DOM.
     */
    mount() {
        if (this._mounted) {
            this._refreshState();
            return;
        }
        const pauseContent = document.querySelector('#overlay-pause .pause-content');
        if (!pauseContent) return;

        // Only Patreon builds get the Toys section — silent no-op otherwise.
        // (The VPS /api/lovense/qr endpoint also rejects unconfigured servers
        // with 503, but the UI should never get that far on a free build.)
        if (!isPat()) return;

        this._root = this._buildSection();
        // Insert before the pause-footer so Toys sit between language/misc settings
        // and the bug report / quit buttons.
        const footer = pauseContent.querySelector('.pause-footer');
        if (footer) pauseContent.insertBefore(this._root, footer);
        else pauseContent.appendChild(this._root);

        this._scope = events.createScope('lovense-settings');
        this._scope.on(Events.LOVENSE_PAIRED, () => this._refreshState());
        this._scope.on(Events.LOVENSE_DISCONNECTED, () => this._refreshState());
        this._scope.on(Events.LOVENSE_TOY_LIST, () => this._refreshState());

        // Start / stop health polling based on pause overlay visibility.
        // The pause overlay uses .hidden class — when open, the class is
        // removed. Health polls only happen while the UI is visible so the
        // user gets disconnect feedback within ~10s of unplugging the toy,
        // without wasting HTTP to the VPS when nothing is visible.
        this._scope.on(Events.OVERLAY_OPEN, (id) => {
            if (id === 'overlay-pause') this._startHealthPoll();
        });
        this._scope.on(Events.OVERLAY_CLOSE, (id) => {
            if (id === 'overlay-pause') this._stopHealthPoll();
        });

        this._mounted = true;
        this._refreshState();

        // Upgrade the Intensity slider to the custom gsap-slider visual
        // (pink track + thumb + marker) so it matches BGM/SFX/UI Scale etc.
        // bindSettingsSliders() queries `.settings-slider` and skips any
        // element that has already been wrapped (the wrap removes that
        // class). This means calling it multiple times is safe — only our
        // new slider gets picked up.
        try { settingsUI.bindSliders(); } catch(e) { /* non-fatal */ }

        // Pause overlay is already open when mount() runs, so kick off the
        // health poll right away.
        this._startHealthPoll();

        // Best-effort reconnect if a session exists from a previous run
        if (lovenseManager.isEnabled() && lovenseManager.isPaired()) {
            lovenseManager.tryReconnect().catch(() => {});
        }
    }

    _startHealthPoll() {
        if (this._healthTimer) return;
        // Run one immediate check so the UI updates within one network
        // roundtrip of the overlay opening. Then schedule the interval.
        this._doHealthCheck();
        this._healthTimer = setInterval(() => this._doHealthCheck(), HEALTH_POLL_INTERVAL_MS);
    }

    _stopHealthPoll() {
        if (this._healthTimer) {
            clearInterval(this._healthTimer);
            this._healthTimer = null;
        }
    }

    async _doHealthCheck() {
        // No point polling if the user never paired or disabled the feature
        if (!lovenseManager.isEnabled()) return;
        if (!lovenseManager._uid) return;
        // checkHealth() handles the disconnect cleanup + event emission
        // internally; we just trigger it and let the LOVENSE_DISCONNECTED
        // event fire via our scope to refresh the UI.
        try {
            await lovenseManager.checkHealth();
        } catch(e) { /* silent — next tick will retry */ }
    }

    /**
     * Tear down the section (used on Patreon revocation or manual
     * disable — currently not triggered anywhere but good hygiene).
     */
    unmount() {
        if (!this._mounted) return;
        this._stopHealthPoll();
        if (this._scope) this._scope.destroy();
        this._scope = null;
        this._pollHandle = null;
        if (this._root && this._root.parentNode) this._root.parentNode.removeChild(this._root);
        this._root = null;
        this._closeQrModal();
        this._mounted = false;
    }

    // ── Section DOM build ─────────────────────────────────

    _buildSection() {
        const section = document.createElement('div');
        section.className = 'pause-lovense';
        section.innerHTML = `
            <div class="panel-divider"></div>
            <div class="settings-header-row">
                <h3 class="settings-header" data-l10n="lovense.header">Toys</h3>
                <span class="settings-badge settings-badge-patreon">Patreon</span>
            </div>

            <div class="settings-row">
                <label>
                    <span data-l10n="lovense.enable">Lovense Integration</span>
                    <span class="settings-hint" data-l10n="lovense.enable_hint">QR-pair your Lovense Remote</span>
                </label>
                <label class="toggle-switch">
                    <input type="checkbox" id="lovense-enable">
                    <span class="toggle-slider"></span>
                </label>
            </div>

            <div class="lovense-status-row" id="lovense-status-row">
                <span class="lovense-status-label" data-l10n="lovense.status">Status</span>
                <span class="lovense-status-value" id="lovense-status-value">—</span>
            </div>

            <div class="lovense-actions">
                <button class="btn-lovense-action" id="btn-lovense-pair" data-l10n="lovense.pair">Pair via QR</button>
                <button class="btn-lovense-action" id="btn-lovense-test" data-l10n="lovense.test">Test Pulse</button>
                <button class="btn-lovense-action" id="btn-lovense-test-pattern" data-l10n="lovense.test_pattern">Test Pattern (emulated)</button>
                <button class="btn-lovense-action" id="btn-lovense-test-wave" data-l10n="lovense.test_wave">Test Wave (emulated)</button>
                <button class="btn-lovense-action btn-lovense-danger" id="btn-lovense-disconnect" data-l10n="lovense.disconnect">Disconnect</button>
            </div>

            <div class="settings-row lovense-row-advanced">
                <label data-l10n="lovense.intensity" title="Master volume, scales all pulses">Intensity</label>
                <input type="range" class="settings-slider" id="lovense-intensity" min="0" max="100" value="80">
                <span class="settings-value" id="lovense-intensity-value">80%</span>
            </div>

            <div class="settings-row lovense-row-advanced">
                <label>
                    <span data-l10n="lovense.ambient">Continuous base pulse</span>
                    <span class="settings-hint" data-l10n="lovense.ambient_hint">stays on between beats, scales with arousal</span>
                </label>
                <label class="toggle-switch"><input type="checkbox" id="lovense-ambient"><span class="toggle-slider"></span></label>
            </div>

            <div class="settings-row lovense-row-advanced">
                <label>
                    <span data-l10n="lovense.auto_arousal">Spike on arousal gain</span>
                    <span class="settings-hint" data-l10n="lovense.auto_arousal_hint">stronger pulse on arousal increase</span>
                </label>
                <label class="toggle-switch"><input type="checkbox" id="lovense-auto-arousal"><span class="toggle-slider"></span></label>
            </div>

            <div class="settings-row lovense-row-advanced">
                <label>
                    <span data-l10n="lovense.vibrate_only">Vibrate only</span>
                    <span class="settings-hint" data-l10n="lovense.vibrate_only_hint">multi-function toys (Nora, Flexer) only buzz, no rotate / thrust / suck</span>
                </label>
                <label class="toggle-switch"><input type="checkbox" id="lovense-vibrate-only"><span class="toggle-slider"></span></label>
            </div>

            <div class="settings-row lovense-row-advanced">
                <label>
                    <span data-l10n="lovense.base_pattern">Base Pattern</span>
                    <span class="settings-hint" data-l10n="lovense.base_pattern_hint">how the toy feels between story beats</span>
                </label>
                <select id="lovense-base-pattern" style="background:#1a1a2e;color:#e0e0e0;border:1px solid #333;border-radius:6px;padding:4px 8px;font-size:0.85rem;">
                    <option value="steady" data-l10n="lovense.pat_steady">Steady</option>
                    <option value="wave" selected data-l10n="lovense.pat_wave">Wave</option>
                    <option value="pulse" data-l10n="lovense.pat_pulse">Pulse</option>
                    <option value="heartbeat" data-l10n="lovense.pat_heartbeat">Heartbeat</option>
                </select>
            </div>
            <!-- Master commands + Climax pattern toggles removed in Phase 3e
                 because the triggers that would consume them (Phase 3f) are
                 not wired yet. Manager API + storage keys are kept so when
                 the triggers ship, re-adding the UI rows is a copy-paste. -->
        `;
        this._bindSectionEvents(section);
        return section;
    }

    _bindSectionEvents(section) {
        const $ = (id) => section.querySelector('#' + id);

        const enableCb = $('lovense-enable');
        enableCb.addEventListener('change', () => {
            localStorage.setItem('PnO_LovenseEnabled', enableCb.checked ? 'true' : 'false');
            this._refreshState();
        });

        $('btn-lovense-pair').addEventListener('click', () => this._openQrModal());
        $('btn-lovense-test').addEventListener('click', () => this._testPulse());
        $('btn-lovense-test-pattern').addEventListener('click', () => this._testPattern());
        $('btn-lovense-test-wave').addEventListener('click', () => this._testWaveEmulated());
        $('btn-lovense-disconnect').addEventListener('click', () => this._disconnect());

        const slider = $('lovense-intensity');
        const sliderVal = $('lovense-intensity-value');
        slider.addEventListener('input', () => {
            const v = parseInt(slider.value, 10);
            sliderVal.textContent = v + '%';
            lovenseManager.setIntensity(v);
        });

        const toggleMap = {
            'lovense-auto-arousal': 'autoArousal',
            'lovense-ambient': 'ambient',
            // Phase 3f will re-add: 'lovense-master-pulse': 'masterPulse',
            //                       'lovense-climax': 'climax',
        };
        for (const [id, cat] of Object.entries(toggleMap)) {
            const el = $(id);
            el.addEventListener('change', () => {
                lovenseManager.setCategoryToggle(cat, el.checked);
                // Turning off the base toggle should immediately stop the
                // sustained pulse so the user can feel the effect of the
                // toggle change right away without waiting for a state event.
                if (cat === 'ambient' && !el.checked) {
                    try { lovenseManager.stopBase(); } catch(e) {}
                }
            });
        }

        // Vibrate-only toggle is its own thing (not a category) — drives the
        // Function action verb (`Vibrate:N` vs `All:N`) and the Pattern feature
        // string. Default OFF = All-mode = inclusive across all toy types.
        $('lovense-vibrate-only').addEventListener('change', (e) => {
            lovenseManager.setVibrateOnly(e.target.checked);
        });

        // Base-pattern selector — switches the sustained pulse between story
        // beats from a flat constant to a looping wave/pulse/heartbeat. The
        // manager restarts the loop immediately on change so the user feels
        // the switch without waiting for the next 25s refresh tick.
        $('lovense-base-pattern').addEventListener('change', (e) => {
            lovenseManager.setBasePattern(e.target.value);
        });
    }

    _refreshState() {
        if (!this._root) return;
        const $ = (id) => this._root.querySelector('#' + id);

        const enabled = lovenseManager.isEnabled();
        const paired = lovenseManager.isPaired();
        const toys = lovenseManager.getToys();

        $('lovense-enable').checked = enabled;

        // Status line
        const statusEl = $('lovense-status-value');
        if (!enabled) {
            statusEl.textContent = L.get('lovense.status_off') || 'Disabled';
            statusEl.className = 'lovense-status-value lovense-status-off';
        } else if (!paired) {
            statusEl.textContent = L.get('lovense.status_not_paired') || 'Not Paired';
            statusEl.className = 'lovense-status-value lovense-status-off';
        } else {
            const names = Object.values(toys).map(t => t.name || t.id).join(', ');
            statusEl.textContent = (L.get('lovense.status_connected') || 'Connected') + ': ' + names;
            statusEl.className = 'lovense-status-value lovense-status-on';
        }

        // Action buttons — pair shown when not paired, disconnect+test when paired
        $('btn-lovense-pair').style.display = paired ? 'none' : '';
        $('btn-lovense-test').style.display = paired ? '' : 'none';
        $('btn-lovense-test-pattern').style.display = paired ? '' : 'none';
        $('btn-lovense-test-wave').style.display = paired ? '' : 'none';
        $('btn-lovense-disconnect').style.display = paired ? '' : 'none';

        // Advanced rows only visible when enabled (reduces clutter on first open)
        this._root.querySelectorAll('.lovense-row-advanced').forEach(row => {
            row.style.display = enabled ? '' : 'none';
        });

        // Sliders + toggles reflect stored state. After settingsUI.bindSliders()
        // has wrapped the native input in a gsap-slider, simply setting .value
        // doesn't sync the visual track/thumb/marker — we need to dispatch an
        // `input` event so both the gsap-slider listener AND our own handler
        // catch up. The input handler no-ops if the value matches what's
        // already in localStorage so there's no feedback loop.
        const slider = $('lovense-intensity');
        const intensity = lovenseManager.getIntensity();
        if (slider.value !== String(intensity)) {
            slider.value = String(intensity);
            slider.dispatchEvent(new Event('input', { bubbles: true }));
        }
        $('lovense-intensity-value').textContent = intensity + '%';
        $('lovense-auto-arousal').checked = lovenseManager.getCategoryToggle('autoArousal');
        $('lovense-ambient').checked = lovenseManager.getCategoryToggle('ambient');
        $('lovense-vibrate-only').checked = lovenseManager.getVibrateOnly();
        $('lovense-base-pattern').value = lovenseManager.getBasePattern();
        // Phase 3f will re-add: master-pulse + climax toggles.
    }

    // ── QR Modal ──────────────────────────────────────────

    _openQrModal() {
        if (this._modal) return;  // already open
        const modal = document.createElement('div');
        modal.id = 'overlay-lovense-qr';
        modal.className = 'overlay overlay-lovense-qr';
        modal.innerHTML = `
            <div class="overlay-box lovense-qr-box">
                <div class="overlay-header">
                    <h2 data-l10n="lovense.qr_title">Pair your toy</h2>
                    <button class="btn-overlay-close" id="lovense-qr-close" aria-label="Close">&times;</button>
                </div>
                <div class="lovense-qr-body">
                    <p class="lovense-qr-instructions" data-l10n="lovense.qr_instructions">
                        Open the Lovense Remote app on your phone, tap Discover, and scan the QR code below.
                    </p>
                    <div class="lovense-qr-image-wrap" id="lovense-qr-image-wrap">
                        <div class="lovense-qr-spinner"></div>
                        <span data-l10n="lovense.qr_loading">Requesting QR...</span>
                    </div>
                    <p class="lovense-qr-note" data-l10n="lovense.qr_note">QR expires in 4 minutes. The pair status will update automatically when your toy connects.</p>
                    <p class="lovense-qr-error" id="lovense-qr-error"></p>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        this._modal = modal;
        modal.querySelector('#lovense-qr-close').addEventListener('click', () => this._closeQrModal());
        modal.addEventListener('click', (e) => {
            if (e.target === modal) this._closeQrModal();
        });

        this._runPairFlow();
    }

    _closeQrModal() {
        if (!this._modal) return;
        lovenseManager.cancelPolling();
        if (this._modal.parentNode) this._modal.parentNode.removeChild(this._modal);
        this._modal = null;
        this._pollHandle = null;
    }

    async _runPairFlow() {
        const modal = this._modal;
        if (!modal) return;
        const wrap = modal.querySelector('#lovense-qr-image-wrap');
        const errorEl = modal.querySelector('#lovense-qr-error');

        try {
            const { qr, code } = await lovenseManager.requestQr('PnOPlayer');
            if (!this._modal) return;  // user closed mid-request
            if (!qr) throw new Error('no QR url in response');
            wrap.innerHTML = `
                <img class="lovense-qr-image" src="${qr}" alt="Pairing QR">
                ${code ? `<div class="lovense-qr-code">Code: ${code}</div>` : ''}
            `;
        } catch(e) {
            if (!this._modal) return;
            errorEl.textContent = (L.get('lovense.qr_error') || 'Could not request QR: ') + (e.message || e);
            return;
        }

        // Poll for pairing
        this._pollHandle = lovenseManager.waitForPairing();
        try {
            await this._pollHandle;
            if (!this._modal) return;
            // Paired — enable the integration if the user hadn't yet, then close
            if (!lovenseManager.isEnabled()) {
                localStorage.setItem('PnO_LovenseEnabled', 'true');
            }
            this._refreshState();
            this._closeQrModal();
            events.emit(Events.TOAST_REQUEST, L.get('lovense.toast_paired') || 'Toy connected', 'pno-toast-success');
        } catch(e) {
            if (!this._modal) return;
            if (e && e.message === 'aborted') return;  // user closed
            errorEl.textContent = (L.get('lovense.qr_timeout') || 'Pairing timed out. Try again.') + ' (' + (e.message || e) + ')';
        }
    }

    // ── Test + Disconnect ─────────────────────────────────

    async _testPulse() {
        // Force enable if somehow off — the button only shows when paired
        if (!lovenseManager.isEnabled()) {
            localStorage.setItem('PnO_LovenseEnabled', 'true');
        }
        const ok = await lovenseManager.pulse(10, 2);
        events.emit(Events.TOAST_REQUEST,
            ok ? (L.get('lovense.toast_test') || 'Test pulse sent')
               : (L.get('lovense.toast_test_failed') || 'Pulse failed — toy not reachable'),
            ok ? 'pno-toast-success' : 'pno-toast-error');
    }

    /**
     * Fire a custom triangle-wave pattern — linear ramp 4→20→4 and back.
     * 16 steps × 200ms = 3.2s natural length. Values 0-20 on the Lovense
     * scale, scaled by master-volume inside the manager. Validates both
     * the semicolon strength list and the `rule:'V:1;F:v;S:200#'` shape.
     */
    async _testPattern() {
        if (!lovenseManager.isEnabled()) {
            localStorage.setItem('PnO_LovenseEnabled', 'true');
        }
        const triangle = [4, 8, 12, 16, 20, 16, 12, 8, 4, 8, 12, 16, 20, 16, 12, 8];
        const ok = await lovenseManager.pattern(triangle, 200);
        events.emit(Events.TOAST_REQUEST,
            ok ? (L.get('lovense.toast_test_pattern') || 'Pattern sent: triangle')
               : (L.get('lovense.toast_test_failed') || 'Pattern failed — toy not reachable'),
            ok ? 'pno-toast-success' : 'pno-toast-error');
    }

    /**
     * Emulate Lovense's built-in `wave` preset via the custom `Pattern`
     * command so it honors the intensity slider. 32 steps × 150ms = 4.8s,
     * same length as `_testPreset` so the A/B comparison is fair.
     *
     * Shape: one full sine cycle at 16 samples, then a second cycle at the
     * same granularity — mean 11, amplitude 9 → values 2-20. Computed
     * ahead of time (not at runtime) to keep the payload build cheap and
     * the semicolon list deterministic for debugging network replays:
     *
     *     round(11 + 9 * sin(2π * i / 16))  for i = 0..15
     *
     *   → 11,14,17,19,20,19,17,14,11,8,5,3,2,3,5,8
     *
     * The second cycle is just the first repeated so two distinct wave
     * swells land inside the 4.8s window — matches how Lovense's native
     * wave feels (multiple rolling swells rather than one long ramp).
     *
     * If this feels close enough to `_testPreset` at the SAME slider
     * position, we know pattern-emulation is a viable path to give the
     * slider control over all presets. If slider at 30% makes this feel
     * noticeably tamer than the native preset at the same slider position,
     * the emulation + slider scaling is working as intended.
     */
    async _testWaveEmulated() {
        if (!lovenseManager.isEnabled()) {
            localStorage.setItem('PnO_LovenseEnabled', 'true');
        }
        const cycle = [11, 14, 17, 19, 20, 19, 17, 14, 11, 8, 5, 3, 2, 3, 5, 8];
        const waveSine = [...cycle, ...cycle];  // 32 steps, 4.8s at 150ms
        const ok = await lovenseManager.pattern(waveSine, 150);
        events.emit(Events.TOAST_REQUEST,
            ok ? (L.get('lovense.toast_test_wave') || 'Wave sent: emulated (slider-scaled)')
               : (L.get('lovense.toast_test_failed') || 'Wave failed — toy not reachable'),
            ok ? 'pno-toast-success' : 'pno-toast-error');
    }

    async _disconnect() {
        await lovenseManager.disconnect();
        this._refreshState();
        events.emit(Events.TOAST_REQUEST, L.get('lovense.toast_disconnected') || 'Toy disconnected', 'pno-toast-info');
    }
}

// Singleton
export const lovenseSettingsUI = new LovenseSettingsUI();

// Expose for test harness
if (typeof window !== 'undefined') {
    window._lovenseSettings = lovenseSettingsUI;
}
